import { DateWithZone } from '@/models/DateWithZone';
import { DateTime } from 'luxon';
import { isEmptyValue } from '@/utils/url';

/**
 * 文字列をキャメルケースに変換する。
 */
export const camelCaseLabel = (str: string) => {
  str = str.charAt(0).toLowerCase() + str.slice(1);
  return str.replace(/[-_](.)/g, (match: any, group: any) => {
    return group.toUpperCase();
  });
};

/**
 * 文字列をスネークケースに変換する
 */
export const snakeCaseLabel = (str: string): string => {
  return str.replace(/[A-Z]/g, (s) => {
    return '_' + s.charAt(0).toLowerCase();
  });
};

/**
 * オブジェクトのキーをスネークケースに変換する
 *
 * @param options.omitEmptyString - 空文字を除去する（デフォルト: true）
 *
 * null/undefined は常に除去される。
 */
export type SnakeCasesOptions = {
  omitEmptyString?: boolean;
};

export const snakeCases = (params: any, options?: SnakeCasesOptions) => {
  if (params === undefined || params === null) {
    return {};
  }
  const opts = { omitEmptyString: true, ...options };
  return convertKeyToSnakeCase(params, opts);
};

const convertKeyToSnakeCase = (
  params: any,
  opts: { omitEmptyString: boolean }
) => {
  const jsonParams: any = {};
  for (const [key, v] of Object.entries(params)) {
    const value = v as any;
    const snakeKey = snakeCaseLabel(key);
    if (v === undefined || v === null) {
      continue;
    } else if (v === '' && opts.omitEmptyString) {
      continue;
    } else if (Array.isArray(value)) {
      jsonParams[snakeKey] = value;
    } else if (value.constructor === DateWithZone) {
      // サーバ側が+9:00表記だとエラーになってしまうので、UTC表記に変換する
      const date = DateTime.fromISO(value.toISOString(), {
        zone: 'UTC',
      });
      jsonParams[snakeKey] = date.toISO();
    } else if (value.constructor.name === 'File') {
      // undefined.undefinedとつなげてもエラーにはならない
      jsonParams[snakeKey] = value;
    } else if (value.constructor == Date) {
      jsonParams[snakeKey] = value.toISOString();
    } else if (typeof value === 'object') {
      jsonParams[snakeKey] = convertKeyToSnakeCase(value, opts);
    } else {
      jsonParams[snakeKey] = value;
    }
  }
  return jsonParams;
};

/**
 * オブジェクトのキーをキャメルケースに変換する
 */
export const camelCases = (params: any) => {
  if (params === undefined || params === null) {
    return {};
  }
  const jsonParams = convertKeyToCamelCase(params);
  return jsonParams;
};

const convertKeyToCamelCase = (params: any) => {
  const jsonParams: any = {};
  for (const [key, v] of Object.entries(params)) {
    const value = v as any;
    if (isEmptyValue(v)) {
      continue;
    } else if (Array.isArray(value)) {
      jsonParams[camelCaseLabel(key)] = value;
    } else if (value.constructor.name === 'File') {
      // undefined.undefinedとつなげてもエラーにはならない
      jsonParams[camelCaseLabel(key)] = value;
    } else if (value.constructor == Date) {
      jsonParams[camelCaseLabel(key)] = value.toISOString();
    } else if (typeof value === 'object') {
      const childValue = convertKeyToCamelCase(value);
      jsonParams[camelCaseLabel(key)] = childValue;
    } else {
      jsonParams[camelCaseLabel(key)] = value;
    }
  }
  return jsonParams;
};
